import Alert from '@xo-union/tk-component-alerts';
import Spinner from '@xo-union/tk-component-spinner';
import React, { ChangeEvent, useContext, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import Honeybadger from 'honeybadger-js';

import { PhotoItemsFragment, useCreatePhotoItemMutation } from '../../../generated/graphql';
import addItemToPage from '../../../helpers/apolloCache/addItemToPage';
import { loadImage, useImageUploader } from '../../../helpers/images/useImageUploader';
import { useManageItem } from '../../../helpers/useManageItem';
import { pagesQuery } from '../../LeftContainer/graphql';
import PhotoCropper from '../../Modals/PhotoCropper';
import { RightRailContext } from '../../PageItemsRail';
import { CropProps, imageFileTypesWithoutGif } from '../ImageField';
import { PageItemIcon } from '../PageItemIcon';
import styles from '../pageItemSelector.scss';

interface PhotoItemFieldProps {
  pageId: number;
}
const PhotoItemField = ({ pageId }: PhotoItemFieldProps) => {
  const { disableOverlay, enableOverlay } = useContext(RightRailContext);
  const { imageUpload } = useImageUploader();
  const alertRef = useRef<any>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const { close } = useManageItem();
  const [createPhoto] = useCreatePhotoItemMutation();
  const [cropperFile, setCropperFile] = useState<HTMLImageElement>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { pathname } = useLocation();
  const previewPage = pathname.includes('preview');

  const closeCropper = () => {
    setCropperFile(undefined);
    previewPage ? disableOverlay() : enableOverlay();
  };

  const showError = (message: string) => {
    alertRef.current.showAndDismiss({
      type: 'error',
      content: message,
      waitTime: 3000,
    });
  };

  const openCropper = async (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (!files || !files[0]) return;
    const image = await loadImage(files[0]);

    if (image?.error) {
      showError(image.error.message);
      return;
    }

    disableOverlay();
    setLoading(false);
    setCropperFile(image.img);
  };

  const afterCrop = async (info: CropProps) => {
    setSaving(true);

    await saveImage(info);

    closeCropper();
    setSaving(false);
  };

  const saveImage = async ({ file, crop }: CropProps) => {
    const image = await imageUpload({ file });

    if (!image || image.error) {
      showError('There was an error uploading your image. Try again.');
      return;
    }

    const variables = {
      attributes: {
        pageId,
        mediaApiId: image?.data?.mediaApiId || '',
        height: crop.height || 0,
        width: crop.width || 0,
        cropX: crop.x,
        cropY: crop.y,
      },
    };

    try {
      await createPhoto({
        variables,
        update: (cache, { data }) => {
          addItemToPage<PhotoItemsFragment | undefined>(cache, pageId, data?.createPhotoItem, close);
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    } catch (error) {
      Honeybadger.notify(error, 'Error creating photo', {
        itemId: image?.data?.mediaApiId,
        pageId,
        Component: 'PhotoItemField',
      });
    }
  };

  const onKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLLabelElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      inputFileRef?.current?.click();
    }
  }, []);

  return (
    <>
      <label
        className={styles.clickable}
        htmlFor="photo-item-input"
        tabIndex={0}
        onKeyDown={onKeyDown}
        role="button"
        aria-label="Photo"
      >
        <input
          id="photo-item-input"
          type="file"
          accept={imageFileTypesWithoutGif.join(',')}
          multiple={false}
          onChange={openCropper}
          style={{ display: 'none' }}
          data-testid="single-image-upload-input"
        />
        {loading ? (
          <div className={styles.loading}>
            <Spinner data-testid="loading-spinner" />
          </div>
        ) : (
          <PageItemIcon icon="PhotoItem" />
        )}
      </label>
      <Alert ref={alertRef} position="bottom" />
      {cropperFile && (
        <PhotoCropper
          cropperInfo={{
            file: cropperFile,
            onSave: afterCrop,
          }}
          onCancel={closeCropper}
          pageId={pageId}
          itemType="PhotoItem"
          loading={saving}
        />
      )}
    </>
  );
};

export default PhotoItemField;
